import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { CheckCircle2, Package } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const { t } = useLang();
  const orderNum = params.get("order") || params.get("order_number");
  const sessionId = params.get("session_id");
  const isPaypalReturn = params.get("paypal") === "1";
  const paypalOrderId = params.get("token"); // PayPal appends ?token=<order_id>&PayerID=<...>
  const [status, setStatus] = useState(null);
  const [paypalCapturing, setPaypalCapturing] = useState(isPaypalReturn);
  const [paypalError, setPaypalError] = useState("");
  const { clear } = useCart();

  // PayPal return: capture the payment server-side before showing the success UI.
  useEffect(() => {
    if (!isPaypalReturn || !orderNum || !paypalOrderId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/paypal/capture", { order_number: orderNum, paypal_order_id: paypalOrderId });
        if (cancelled) return;
        if (r.data?.ok) {
          setStatus({
            order_number: orderNum,
            payment_status: "paid",
            total: r.data.order?.total,
            items: r.data.order?.items,
          });
          clear();
        } else {
          setPaypalError(t("success_paypal_error_generic"));
        }
      } catch (e) {
        if (!cancelled) setPaypalError(e.response?.data?.detail || t("success_paypal_error_capture"));
      } finally {
        if (!cancelled) setPaypalCapturing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isPaypalReturn, orderNum, paypalOrderId]); // eslint-disable-line

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts++;
      try {
        const r = await api.get(`/orders/status/${sessionId}`);
        if (cancelled) return;
        setStatus(r.data);
        if (r.data.payment_status === "paid") {
          clear();
          return;
        }
      } catch { /* keep polling */ }
      if (attempts < 10 && !cancelled) setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line

  useEffect(() => {
    if (orderNum) clear();
    // Fire Purchase event on the thank-you page — MUST include the same event_id
    // that was passed to the backend in POST /orders so Meta deduplicates the
    // client-side (with fbc/fbp cookies) and server-side (CAPI, with hashed PII)
    // events into a SINGLE conversion. Without a shared event_id Meta counted them
    // as two separate signals and dropped the CAPI one that lacked fbc — the exact
    // bug that made Purchase fbc coverage collapse to 0%.
    if (orderNum && typeof window !== "undefined" && typeof window.audrivioTrack === "function") {
      let sharedEventId = null;
      try { sharedEventId = sessionStorage.getItem("audrivio_purchase_event_id"); } catch { /* ignore */ }
      window.audrivioTrack("Purchase", {
        currency: "EUR",
        value: Number(status?.total || 0),
        content_ids: (status?.items || []).filter((i) => !i.is_gift).map((i) => i.product_id),
        order_id: orderNum,
        event_id: sharedEventId || undefined,
      });
      // Cleanup — one purchase should fire at most one dedup pair.
      try { if (sharedEventId) sessionStorage.removeItem("audrivio_purchase_event_id"); } catch { /* ignore */ }
    }
  }, [orderNum, status?.total]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-xd-light">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
        {paypalCapturing ? (
          <>
            <div className="w-16 h-16 rounded-full bg-xd-cyan/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <CheckCircle2 className="w-8 h-8 text-xd-cyanHover" />
            </div>
            <h1 className="font-display text-3xl font-semibold">{t("success_paypal_confirming_h1")}</h1>
            <p className="text-xd-muted mt-2">{t("success_paypal_confirming_desc")}</p>
          </>
        ) : paypalError ? (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-display text-3xl font-semibold text-red-700" data-testid="checkout-paypal-error">{t("success_paypal_error_h1")}</h1>
            <p className="text-xd-muted mt-2">{paypalError}</p>
            <Link to="/checkout" className="btn-cyan inline-flex items-center rounded-lg px-6 py-3 mt-8">{t("success_retry_payment")}</Link>
          </>
        ) : (
        <>
        <div className="w-16 h-16 rounded-full bg-xd-success/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-xd-success" />
        </div>
        <h1 className="font-display text-3xl font-semibold" data-testid="checkout-success-title">{t("success_h1")}</h1>
        <p className="text-xd-muted mt-2">{t("success_desc")}</p>

        {status && (
          <div className="mt-6 bg-white border border-xd-border rounded-xl p-6 text-left">
            <div className="flex items-center gap-2 mb-3"><Package className="w-4 h-4 text-xd-cyanHover" /><span className="font-mono-tech text-xs uppercase tracking-widest">{t("success_order_label")} #{status.order_number}</span></div>
            <div className="text-sm text-xd-muted">{t("success_payment_status_label")} <strong className="text-xd-text">{status.payment_status}</strong></div>
            <div className="text-sm text-xd-muted mt-1">{t("success_total_label")} <strong className="text-xd-text">€ {status.total?.toFixed(2)}</strong></div>
          </div>
        )}
        {orderNum && !sessionId && (
          <div className="mt-6 bg-white border border-xd-border rounded-xl p-6 text-left">
            <div className="flex items-center gap-2 mb-3"><Package className="w-4 h-4 text-xd-cyanHover" /><span className="font-mono-tech text-xs uppercase tracking-widest">{t("success_order_label")} #{orderNum}</span></div>
            <div className="text-sm text-xd-muted">{t("success_email_sent")}</div>
          </div>
        )}

        <Link to="/" className="btn-cyan inline-flex items-center rounded-lg px-6 py-3 mt-8">{t("success_back_home")}</Link>
        </>
        )}
      </div>
      <Footer />
    </div>
  );
}
